import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useStateContext } from '../contexts/ContextProvider';
import { Header } from '../components';
import { 
  FaFolder, 
  FaFolderOpen, 
  FaFile, 
  FaPlus, 
  FaEdit, 
  FaArrowLeft, 
  FaChevronRight,
  FaTimes,
  FaSearch,
  FaFilter,
  FaFileAlt,
  FaEllipsisV,
  FaTrash
} from 'react-icons/fa';
import FolderBrowser from '../components/FolderBrowser/FolderBrowser';

const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';

// Define options for dropdowns with display values
const CLASS_OPTIONS = [
  { value: 'class6', display: 'Class 6th' },
  { value: 'class9', display: 'Class 9th' }
];
const SUBJECT_OPTIONS = ['All', 'Maths', 'English', 'Science', 'SST', 'Reasoning'];
const COURSE_TARGET_OPTIONS = ['Sainik School', 'RIMC', 'RMS'];

const Folders = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { currentColor } = useStateContext();
  
  // Extract folder ID from URL parameters
  const folderId = params.folderId || null;
  
  // State variables
  const [currentFolder, setCurrentFolder] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Modal states
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderClass, setFolderClass] = useState('');
  const [folderSubject, setFolderSubject] = useState('');
  const [folderCourseTarget, setFolderCourseTarget] = useState('');
  const [editFolderId, setEditFolderId] = useState(null);
  
  // Filter states
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterCourseTarget, setFilterCourseTarget] = useState('');
  const [isFiltering, setIsFiltering] = useState(false);
  
  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  
  // Load specific folder by ID and fetch tests inside it
  const fetchFolderDetails = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      const folderResponse = await axios.get(`${API_BASE_URL}/Folders/${id}`);
      setCurrentFolder(folderResponse.data);
      
      // Fetch tests using our optimized Stage 1 endpoint (returns count and data if query params are present)
      const testsResponse = await axios.get(`${API_BASE_URL}/Tests?folderId=${id}`);
      const resData = testsResponse.data;
      if (resData && Array.isArray(resData.data)) {
        setTests(resData.data);
      } else if (Array.isArray(resData)) {
        // Safe fallback client-side filter if production API is not updated yet
        setTests(resData.filter(t => t.folder_id === parseInt(id)));
      } else {
        setTests([]);
      }
    } catch (err) {
      console.error('Error fetching folder metadata or tests:', err);
      setError('Failed to load folder details.');
      setCurrentFolder(null);
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  // Sync route param updates with backend queries
  useEffect(() => {
    if (folderId) {
      fetchFolderDetails(folderId);
    } else {
      setCurrentFolder(null);
      setTests([]);
    }
  }, [folderId]);

  const handleFolderChange = (id) => {
    // Navigate triggers params change, which runs the useEffect above
    if (id) {
      navigate(`/folders/${id}`);
    } else {
      navigate('/folders');
    }
  };
  
  const navigateToCreateTest = () => {
    if (currentFolder) {
      navigate(`/tests/create?folderId=${currentFolder.id}`);
    } else {
      setError('Please navigate into a folder first to create a test.');
    }
  };
  
  // Create a new folder
  const createFolder = async () => {
    if (!folderName.trim()) {
      setError('Folder name cannot be empty.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const nowISO = new Date().toISOString();
      
      const newFolder = {
        name: folderName,
        parent_id: currentFolder ? currentFolder.id : null,
        class: folderClass || null,
        subject: folderSubject || null,
        course_target: folderCourseTarget || null,
        created_at: nowISO,
        updated_at: nowISO
      };
      
      const response = await axios.post(`${API_BASE_URL}/Folders`, newFolder);
      console.log('Folder created successfully:', response.data);
      
      // Trigger FolderBrowser reload
      setRefreshKey(prev => prev + 1);
      
      resetFolderForm();
      setShowFolderModal(false);
    } catch (err) {
      console.error('Error creating folder:', err);
      
      if (err.response && err.response.data) {
        console.error('Server response:', err.response.data);
        
        if (err.response.data.message && err.response.data.message.includes('duplicate key')) {
          setError('A folder with this ID already exists. Please try again.');
        } else if (err.response.data.message && err.response.data.message.includes('Validation error')) {
          setError('Validation error: Please check the folder information.');
        } else {
          setError(`Failed to create folder: ${err.response.data.message || 'Unknown error'}`);
        }
      } else {
        setError('Failed to create folder. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Update a folder
  const updateFolder = async () => {
    if (!folderName.trim() || !editFolderId) {
      setError('Folder name cannot be empty.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const updatedFolder = {
        name: folderName,
        class: folderClass,
        subject: folderSubject,
        course_target: folderCourseTarget,
        updated_at: new Date()
      };
      
      await axios.put(`${API_BASE_URL}/Folders/${editFolderId}`, updatedFolder);
      
      // Trigger FolderBrowser reload
      setRefreshKey(prev => prev + 1);
      
      resetFolderForm();
      setEditFolderId(null);
      setShowFolderModal(false);
    } catch (err) {
      setError('Failed to update folder. Please try again.');
      console.error('Error updating folder:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Delete a folder
  const deleteFolder = async () => {
    if (!folderToDelete) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await axios.delete(`${API_BASE_URL}/Folders/${folderToDelete.id}`);
      
      // Trigger FolderBrowser reload
      setRefreshKey(prev => prev + 1);
      
      setShowDeleteConfirm(false);
      setFolderToDelete(null);
    } catch (err) {
      setError('Failed to delete folder. It may contain folders or tests.');
      console.error('Error deleting folder:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Reset folder form
  const resetFolderForm = () => {
    setFolderName('');
    setFolderClass('');
    setFolderSubject('');
    setFolderCourseTarget('');
  };
  
  // Open edit modal
  const openEditModal = (folder) => {
    setFolderName(folder.name);
    setFolderClass(folder.class || '');
    setFolderSubject(folder.subject || '');
    setFolderCourseTarget(folder.course_target || '');
    setEditFolderId(folder.id);
    setShowFolderModal(true);
  };
  
  // Open new folder modal
  const openNewFolderModal = () => {
    resetFolderForm();
    setEditFolderId(null);
    setShowFolderModal(true);
  };
  
  // Open delete confirmation
  const openDeleteConfirm = (folder) => {
    setFolderToDelete(folder);
    setShowDeleteConfirm(true);
  };
  
  // Apply filters
  const applyFilters = () => {
    const shouldFilter = filterClass !== '' || filterSubject !== '' || filterCourseTarget !== '';
    setIsFiltering(shouldFilter);
    setShowFilterModal(false);
    setRefreshKey(prev => prev + 1);
  };
  
  // Clear filters
  const clearFilters = () => {
    setFilterClass('');
    setFilterSubject('');
    setFilterCourseTarget('');
    setIsFiltering(false);
    setShowFilterModal(false);
    setRefreshKey(prev => prev + 1);
  };
  
  // Navigate to a test
  const navigateToTest = (testId) => {
    if (!testId || testId === 'undefined') {
      setError('Cannot navigate to test: Invalid test ID');
      return;
    }
    navigate(`/tests/${testId}`);
  };
  
  // Inline CSS styles
  const styles = {
    container: {
      margin: '0.5rem',
      marginTop: '1.5rem',
      padding: '0.5rem',
      borderRadius: '1.5rem',
      backgroundColor: 'white',
      minHeight: '80vh'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '1.25rem'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      marginRight: '1rem',
      color: '#374151',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      transition: 'background-color 0.2s'
    },
    errorBox: {
      backgroundColor: '#FEE2E2',
      border: '1px solid #F87171',
      color: '#B91C1C',
      padding: '0.75rem 1rem',
      borderRadius: '0.375rem',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    actionsBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    breadcrumbNav: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '0.5rem',
      flex: '1 1 auto'
    },
    breadcrumbs: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    breadcrumbItem: {
      display: 'flex',
      alignItems: 'center'
    },
    breadcrumbLink: {
      color: '#374151',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: '0.2rem 0.5rem',
      borderRadius: '0.25rem',
      textDecoration: 'none',
      transition: 'background-color 0.2s'
    },
    breadcrumbSeparator: {
      margin: '0 0.5rem',
      color: '#6B7280'
    },
    actionsGroup: {
      display: 'flex',
      gap: '0.5rem'
    },
    newFolderButton: {
      padding: '0.5rem 1rem',
      borderRadius: '0.375rem',
      backgroundColor: currentColor,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      border: 'none',
      cursor: 'pointer',
      transition: 'opacity 0.2s'
    },
    filterButton: {
      padding: '0.5rem 1rem',
      borderRadius: '0.375rem',
      backgroundColor: isFiltering ? currentColor : '#F3F4F6',
      color: isFiltering ? 'white' : '#374151',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      border: isFiltering ? 'none' : '1px solid #D1D5DB',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: '2rem 0'
    },
    spinner: {
      display: 'inline-block',
      width: '2rem',
      height: '2rem',
      borderRadius: '50%',
      border: '4px solid #E5E7EB',
      borderTopColor: currentColor,
      animation: 'spin 1s linear infinite'
    },
    sectionTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      marginBottom: '1rem',
      color: '#374151'
    },
    subsectionTitle: {
      fontSize: '1.125rem',
      fontWeight: '500',
      marginBottom: '0.75rem',
      color: '#4B5563'
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '1rem',
      marginBottom: '2rem'
    },
    folderCard: {
      backgroundColor: 'white',
      borderRadius: '0.5rem',
      border: '1px solid #E5E7EB',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s',
      position: 'relative'
    },
    folderCardContent: {
      padding: '1rem'
    },
    folderHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.5rem'
    },
    folderNameArea: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      flex: '1 1 auto',
      minWidth: 0,
      overflow: 'hidden'
    },
    folderIcon: {
      color: '#F59E0B',
      marginRight: '0.75rem',
      fontSize: '1.25rem',
      flexShrink: 0
    },
    folderName: {
      fontWeight: '500',
      color: '#1F2937',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    actionButtonContainer: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      position: 'relative'
    },
    actionButton: {
      color: '#6B7280',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s'
    },
    actionMenu: {
      position: 'absolute',
      top: '100%',
      right: 0,
      backgroundColor: 'white',
      border: '1px solid #E5E7EB',
      borderRadius: '0.5rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      zIndex: 10,
      minWidth: '150px',
      overflow: 'hidden'
    },
    actionMenuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      color: '#374151',
      fontSize: '0.875rem'
    },
    deleteMenuItem: {
      color: '#EF4444'
    },
    folderDate: {
      fontSize: '0.75rem',
      color: '#6B7280',
      marginTop: '0.5rem'
    },
    folderMeta: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '0.5rem',
      flexWrap: 'wrap'
    },
    metaBadge: {
      padding: '0.25rem 0.5rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      backgroundColor: '#F3F4F6',
      color: '#4B5563'
    },
    testCard: {
      backgroundColor: 'white',
      borderRadius: '0.5rem',
      border: '1px solid #E5E7EB',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s',
      cursor: 'pointer'
    },
    testCardContent: {
      padding: '1rem'
    },
    testHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '0.5rem'
    },
    testIcon: {
      color: '#2563EB',
      marginRight: '0.75rem',
      fontSize: '1.25rem'
    },
    testName: {
      fontWeight: '500',
      color: '#1F2937',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    testFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '0.5rem'
    },
    testDate: {
      fontSize: '0.75rem',
      color: '#6B7280'
    },
    statusBadge: {
      padding: '0.25rem 0.5rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    publishedBadge: {
      backgroundColor: '#D1FAE5',
      color: '#065F46'
    },
    draftBadge: {
      backgroundColor: '#FEF3C7',
      color: '#92400E'
    },
    emptyState: {
      backgroundColor: '#F9FAFB',
      border: '1px dashed #D1D5DB',
      borderRadius: '0.5rem',
      padding: '3rem 2rem',
      textAlign: 'center',
      color: '#6B7280'
    },
    emptyStateHeading: {
      fontSize: '1.125rem',
      fontWeight: '500',
      marginBottom: '0.5rem',
      color: '#374151'
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '0.5rem',
      width: '100%',
      maxWidth: '28rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    modalHeader: {
      padding: '1rem 1.5rem',
      borderBottom: '1px solid #E5E7EB',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    modalTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#1F2937'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#6B7280',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      transition: 'background-color 0.2s'
    },
    modalBody: {
      padding: '1.5rem'
    },
    formGroup: {
      marginBottom: '1rem'
    },
    formLabel: {
      display: 'block',
      marginBottom: '0.5rem',
      fontWeight: '500',
      color: '#374151'
    },
    formInput: {
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      fontSize: '1rem',
      transition: 'border-color 0.2s, box-shadow 0.2s'
    },
    formSelect: {
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      fontSize: '1rem',
      backgroundColor: 'white',
      cursor: 'pointer'
    },
    modalFooter: {
      padding: '1rem 1.5rem',
      borderTop: '1px solid #E5E7EB',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem'
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#F3F4F6',
      color: '#1F2937',
      borderRadius: '0.375rem',
      fontWeight: '500',
      border: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    submitButton: {
      padding: '0.5rem 1rem',
      backgroundColor: currentColor,
      color: 'white',
      borderRadius: '0.375rem',
      fontWeight: '500',
      border: 'none',
      cursor: 'pointer',
      transition: 'opacity 0.2s'
    },
    activeFiltersBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      backgroundColor: currentColor,
      color: 'white',
      borderRadius: '9999px',
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: '500',
      marginLeft: '0.5rem'
    },
    deleteConfirmText: {
      marginBottom: '1rem',
      color: '#374151'
    },
    deleteWarning: {
      color: '#EF4444',
      fontSize: '0.875rem',
      marginBottom: '1.5rem'
    },
    deleteButton: {
      backgroundColor: '#EF4444',
      color: 'white'
    }
  };
  
  // Keyframes for spinner animation
  if (!document.getElementById('spinnerAnimation')) {
    const style = document.createElement('style');
    style.id = 'spinnerAnimation';
    style.innerHTML = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }
  
  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white dark:bg-secondary-dark-bg rounded-3xl">
      
      {/* Error display */}
      {error && (
        <div style={styles.errorBox}>
          <FaTimes />
          {error}
        </div>
      )}
      
      <FolderBrowser
        initialFolderId={folderId ? parseInt(folderId) : null}
        onFolderChange={handleFolderChange}
        showActions={true}
        onEditFolder={openEditModal}
        onDeleteFolder={openDeleteConfirm}
        filterClass={filterClass}
        filterSubject={filterSubject}
        filterCourseTarget={filterCourseTarget}
        refreshKey={refreshKey}
        rightActions={
          <div style={styles.actionsGroup}>
            <button 
              type="button"
              onClick={() => setShowFilterModal(true)}
              style={styles.filterButton}
              onMouseOver={(e) => {
                if (!isFiltering) e.currentTarget.style.backgroundColor = '#E5E7EB';
              }}
              onMouseOut={(e) => {
                if (!isFiltering) e.currentTarget.style.backgroundColor = '#F3F4F6';
              }}
            >
              <FaFilter style={{ marginRight: '0.25rem' }} /> 
              Filter
              {isFiltering && (
                <span style={styles.activeFiltersBadge}>
                  Active
                </span>
              )}
            </button>
            
            <button 
              type="button"
              onClick={openNewFolderModal}
              style={styles.newFolderButton}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              <FaPlus style={{ marginRight: '0.25rem' }} /> New Folder
            </button>
            
            {currentFolder && (
              <button 
                type="button"
                onClick={navigateToCreateTest}
                style={{
                  ...styles.newFolderButton,
                  backgroundColor: '#2563EB'
                }}
                onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
              >
                <FaFileAlt style={{ marginRight: '0.25rem' }} /> Create Test
              </button>
            )}
          </div>
        }
      />
      
      {/* Tests section */}
      {tests.length > 0 && (
        <div>
          <h3 style={styles.subsectionTitle}>Tests</h3>
          <div style={styles.gridContainer}>
            {tests.map(test => (
              <div 
                key={test.id || test.test_id}
                style={styles.testCard}
                onClick={() => navigateToTest(test.id || test.test_id)}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                }}
              >
                <div style={styles.testCardContent}>
                  <div style={styles.testHeader}>
                    <FaFile style={styles.testIcon} />
                    <span style={styles.testName}>{test.name}</span>
                  </div>
                  <div style={styles.testFooter}>
                    <span style={styles.testDate}>
                      {new Date(test.updated_at).toLocaleDateString()}
                    </span>
                    <span 
                      style={{
                        ...styles.statusBadge,
                        ...(test.publish_status ? styles.publishedBadge : styles.draftBadge)
                      }}
                    >
                      {test.publish_status ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Empty state */}
      {currentFolder && tests.length === 0 && !loading && (
        <div style={styles.emptyState}>
          <p style={styles.emptyStateHeading}>No tests found in this folder</p>
          <p>Click "Create Test" to add a test</p>
        </div>
      )}
      
      {/* Folder Modal */}
      {showFolderModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editFolderId ? 'Edit Folder' : 'New Folder'}
              </h2>
              <button 
                style={styles.closeButton} 
                onClick={() => setShowFolderModal(false)}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <FaTimes />
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Folder Name</label>
                <input
                  type="text"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  style={styles.formInput}
                  placeholder="Enter folder name"
                  onFocus={(e) => {
                    e.target.style.borderColor = currentColor;
                    e.target.style.boxShadow = `0 0 0 2px ${currentColor}33`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#D1D5DB';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Class</label>
                <select
                  value={folderClass}
                  onChange={(e) => setFolderClass(e.target.value)}
                  style={styles.formSelect}
                >
                  <option value="">Select Class</option>
                  {CLASS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.display}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Subject</label>
                <select
                  value={folderSubject}
                  onChange={(e) => setFolderSubject(e.target.value)}
                  style={styles.formSelect}
                >
                  <option value="">Select Subject</option>
                  {SUBJECT_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Course Target</label>
                <select
                  value={folderCourseTarget}
                  onChange={(e) => setFolderCourseTarget(e.target.value)}
                  style={styles.formSelect}
                >
                  <option value="">Select Course Target</option>
                  {COURSE_TARGET_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div style={styles.modalFooter}>
              <button
                onClick={() => setShowFolderModal(false)}
                style={styles.cancelButton}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#E5E7EB'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
              >
                Cancel
              </button>
              <button
                onClick={editFolderId ? updateFolder : createFolder}
                style={styles.submitButton}
                onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
              >
                {editFolderId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Filter Modal */}
      {showFilterModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                Filter Folders
              </h2>
              <button 
                style={styles.closeButton} 
                onClick={() => setShowFilterModal(false)}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <FaTimes />
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Class</label>
                <select
                  value={filterClass}
                  onChange={(e) => setFilterClass(e.target.value)}
                  style={styles.formSelect}
                >
                  <option value="">All Classes</option>
                  {CLASS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.display}</option>
                  ))}
                </select>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Subject</label>
                <select
                  value={filterSubject}
                  onChange={(e) => setFilterSubject(e.target.value)}
                  style={styles.formSelect}
                >
                  <option value="">All Subjects</option>
                  {SUBJECT_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Course Target</label>
                <select
                  value={filterCourseTarget}
                  onChange={(e) => setFilterCourseTarget(e.target.value)}
                  style={styles.formSelect}
                >
                  <option value="">All Course Targets</option>
                  {COURSE_TARGET_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div style={styles.modalFooter}>
              <button
                onClick={clearFilters}
                style={styles.cancelButton}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#E5E7EB'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
              >
                Clear Filters
              </button>
              <button
                onClick={applyFilters}
                style={styles.submitButton}
                onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Delete Folder</h2>
              <button 
                style={styles.closeButton} 
                onClick={() => setShowDeleteConfirm(false)}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <FaTimes />
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <p style={styles.deleteConfirmText}>
                Are you sure you want to delete "{folderToDelete?.name}"?
              </p>
              <p style={styles.deleteWarning}>
                This action cannot be undone. The folder must be empty (no subfolders or tests) to be deleted.
              </p>
            </div>
            
            <div style={styles.modalFooter}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={styles.cancelButton}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#E5E7EB'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
              >
                Cancel
              </button>
              <button
                onClick={deleteFolder}
                style={{...styles.submitButton, ...styles.deleteButton}}
                onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Folders;